import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import { drawColumns, drawGroundPosition, drawRows, getHeight, getWidth, PigeonholeMapGroundPosition } from './ground-cell';
import { MapViewComponent } from '../../../shared/map-view/map-view.component';
import { MatDialog } from '@angular/material/dialog';
import { TaskRequestSidenavService } from 'src/app/core/services/task-request-sidenav.service';
import { PigeonholeTooltipContent } from '../../map/map-tooltip/map-tooltip.types';
import { TooltipDimensions } from '../../map/map-tooltip/map-tooltip.component';
import { PigeonholeTooltipService } from '../../../core/services/pigeonhole-tooltip.service';
import { PigeonholeService } from 'src/app/core/data-sources/v3/pigeonhole/pigeonhole.service';
import { PigeonholePackage, PigeonholePositionInfo } from 'src/models/Pigeonhole';
import { PigeonholeMapPositionDetailsModalComponent } from 'src/app/modals/pigeonhole-map-position-details-modal/pigeonhole-map-position-details-modal.component';
import { PigeonholeRowColumnModalComponent } from 'src/app/modals/pigeonhole-row-column-modal/pigeonhole-row-column-modal.component';

export enum MouseQuadrant {
  UNDEFINED = 0,
  TOP_LEFT = 1,
  TOP_RIGHT = 2,
  BOTTOM_RIGHT = 3,
  BOTTOM_LEFT = 4
}

function calcCanvasMousePosition(canvas: HTMLCanvasElement, mouseX: number, mouseY: number) {
  const canvasTop = canvas.getBoundingClientRect().top;
  const canvasLeft = canvas.getBoundingClientRect().left;
  return {
    canvasMouseX: mouseX - canvasLeft,
    canvasMouseY: mouseY - canvasTop
  }
}

function calcQuadrant(canvas: HTMLCanvasElement, canvasMouseX: number, canvasMouseY: number) {
  const canvasWidth = canvas.getBoundingClientRect().width;
  const canvasHeight = canvas.getBoundingClientRect().height;
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;
  
  let mouseQuadrant: MouseQuadrant = MouseQuadrant.UNDEFINED;

  if (canvasMouseX < centerX && canvasMouseY < centerY) {
    mouseQuadrant = MouseQuadrant.TOP_LEFT;
  } else if (canvasMouseX >= centerX && canvasMouseY < centerY) {
    mouseQuadrant = MouseQuadrant.TOP_RIGHT;
  } else if (canvasMouseX >= centerX && canvasMouseY >= centerY) {
    mouseQuadrant = MouseQuadrant.BOTTOM_RIGHT;
  } else {
    mouseQuadrant = MouseQuadrant.BOTTOM_LEFT;
  }
  return mouseQuadrant;
}

/**
 * Componente de mapa com orientação invertida (Y crescendo de baixo para cima)
 * A1 está localizado no canto inferior esquerdo
 */
@Component({
  selector: 'app-pigeonhole-map-position-view',
  templateUrl: './pigeonhole-map-position-view.component.html',
  styleUrls: ['./pigeonhole-map-position-view.component.scss']
})
export class PigeonholeMapPositionViewComponent extends MapViewComponent implements OnInit, OnChanges {

  public maxY = 0;
  @Input() map;
  @Input() id: string;
  @Input() openTip = false;
  @Input() positionInfo: PigeonholePositionInfo;
  public mouseX;
  public mouseY;
  public mouseQuadrant: MouseQuadrant = MouseQuadrant.UNDEFINED;
  public showTooltip;
  public tooltipValues: PigeonholeTooltipContent = null;
  public isMapEmpty = false;
  public timeout;
  public toggleTip = false;
  private tooltipWidth = 148;
  private tooltipHeight = 100;

  @Output() mapLoaded = new EventEmitter();
  @Output() toggleTipEvent = new EventEmitter<boolean>(false);

  @ViewChild('canvas', { static: false }) canvas: ElementRef;
  @ViewChild('tooltipElement', { static: false }) tooltipElement: ElementRef;

  @HostListener('window:resize') public resize() {
    super.resize();
  }

  @HostListener('mousemove', ['$event']) 
  onMouseMove(event) {
    const canvas: HTMLCanvasElement = (this.elementRef.nativeElement as HTMLElement).querySelector('canvas');
    if (!canvas) return;
    const canvasMousePosition = calcCanvasMousePosition(canvas, event.clientX, event.clientY);
    this.mouseX = canvasMousePosition.canvasMouseX;
    this.mouseY = canvasMousePosition.canvasMouseY;
    this.mouseQuadrant = calcQuadrant(canvas, canvasMousePosition.canvasMouseX, canvasMousePosition.canvasMouseY);
  }

  constructor(
    protected elementRef: ElementRef,
    protected ngZone: NgZone,
    private pigeonholeService: PigeonholeService,
    private dialog: MatDialog,
    private sidenavService: TaskRequestSidenavService,
    private cdr: ChangeDetectorRef,
    private pigeonholeTooltipService: PigeonholeTooltipService
  ) {
    super(elementRef, ngZone);
  }

  public getTooltipPosition() {
    const tooltipOffset = 20;
    
    if (this.tooltipWidth <= 0) this.tooltipWidth = 150;
    if (this.tooltipHeight <= 0) this.tooltipHeight = 100;
    
    const canvas = (this.elementRef.nativeElement as HTMLElement).querySelector('canvas');
    if (canvas) {
      const canvasRect = canvas.getBoundingClientRect();
      const canvasWidth = canvasRect.width;
      const canvasHeight = canvasRect.height;
      
      let leftPos, topPos;
      
      switch (this.mouseQuadrant) {
        case MouseQuadrant.TOP_LEFT:
          leftPos = this.mouseX + tooltipOffset;
          topPos = this.mouseY + tooltipOffset;
          break;
        case MouseQuadrant.TOP_RIGHT:
          leftPos = this.mouseX - this.tooltipWidth;
          topPos = this.mouseY + tooltipOffset;
          break;
        case MouseQuadrant.BOTTOM_RIGHT:
          leftPos = this.mouseX - this.tooltipWidth;
          topPos = this.mouseY - this.tooltipHeight;
          break;
        case MouseQuadrant.BOTTOM_LEFT:
          leftPos = this.mouseX + tooltipOffset;
          topPos = this.mouseY - this.tooltipHeight;
          break;
        default:
          leftPos = this.mouseX + tooltipOffset;
          topPos = this.mouseY + tooltipOffset;
      }
      
      if (leftPos < 0) leftPos = 0;
      if (topPos < 0) topPos = 0;
      if (leftPos + this.tooltipWidth > canvasWidth) {
        leftPos = canvasWidth - this.tooltipWidth;
      }
      if (topPos + this.tooltipHeight > canvasHeight) {
        topPos = canvasHeight - this.tooltipHeight;
      }
      
      return {
        'top.px': topPos,
        'left.px': leftPos
      };
    }
    
    return {
      'top.px': this.mouseY + tooltipOffset,
      'left.px': this.mouseX + tooltipOffset
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.map?.currentValue) {
      if (!changes.map.currentValue?.length) {
        this.isMapEmpty = true;
        this.mapLoaded.emit();
        return;
      }

      const gradePositions = this.getMaxCoordinates(changes.map.currentValue);
      const maxWidth = getWidth(gradePositions.positionsX.length);
      const maxHeight = getHeight(gradePositions.positionsY.length);
      this.maxY = gradePositions.positionsY.length - 1;

      this.pigeonholeService.getPigeonholePackages(this.id).then(res => {
        this.processPackages(res);
      });

      this.initMap(maxWidth, maxHeight);
      this.drawMap(changes.map.currentValue);
      
      setTimeout(() => {
        if (this.viewPort) {
          const border = 40;
          const viewportHeight = this.viewPort.screenHeight;
          const worldHeight = maxHeight + 2 * border;
          this.viewPort.moveCorner(0, worldHeight - viewportHeight);
        }
        this.mapLoaded.emit();
      }, 300);
    }
    if (changes.openTip?.currentValue) {
      this.toggleTip = changes.openTip.currentValue;
    }
  }

  ngOnInit(): void {}

  public processPackages(packages: PigeonholePackage[]) {
    this.pigeonholeTooltipService.processCratesAndLevels(packages);
  }

  public getToolTipValues(pigeonholeId: string, groundPositionType?: string) {
    this.tooltipValues = this.pigeonholeTooltipService.getTooltipContent(pigeonholeId, groundPositionType);
  }

  public drawMap(groundPositions: PigeonholeMapGroundPosition[]) {
    if (groundPositions) {
      this.drawGrades(groundPositions);
      this.drawDefaultMap(groundPositions);
    }
  }

  drawGrades(groundPositions) {
    const gradePositions = this.getMaxCoordinates(groundPositions);
    const columns = drawColumns(gradePositions.positionsX);
    for (const c of columns) {
      c.interactive = true;
      c.cursor = 'pointer';
      c.on('click', () => {
        this.openRowColumnModal('column', c.text);
      });
      // @ts-ignore
      this.viewPort.addChild(c);
    }
    const rows = drawRows(gradePositions.positionsY, this.maxY);
    for (const r of rows) {
      r.interactive = true;
      r.cursor = 'pointer';
      r.on('click', () => {
        this.openRowColumnModal('row', r.text);
      });
      // @ts-ignore
      this.viewPort.addChild(r);
    }
  }

  openRowColumnModal(type: 'row' | 'column', coordinate?: number) {
    this.dialog.open(PigeonholeRowColumnModalComponent, {
      panelClass: 'details-modal',
      data: { type, coordinate, id: this.id }
    });
  }

  onTooltipDimensionsChange(dimensions: TooltipDimensions) {
    this.tooltipWidth = dimensions.width;
    this.tooltipHeight = dimensions.height;
    this.cdr.detectChanges();
  }

  openModal(qrCode: string) {
    this.dialog.open(PigeonholeMapPositionDetailsModalComponent, {
      panelClass: 'details-modal',
      data: { qrCode, positionInfo: this.positionInfo }
    });
  }

  drawDefaultMap(groundPositions) {
    for (const groundPosition of groundPositions) {
      const elements = drawGroundPosition(groundPosition, this.maxY);
      const area = elements[0];
      area.interactive = true;
      area.cursor = 'pointer';

      area.on('pointerover', () => {
        if (groundPosition.hasPallet) {
          this.getToolTipValues(groundPosition.id, groundPosition.type);
        } else {
          this.tooltipValues = { pallets: [], palletQty: 0, empty: true };
        }
        if (this.tooltipValues) {
          this.showTooltip = true;
          if (this.timeout) {
            clearTimeout(this.timeout);
          }
        }
      });

      area.on('click', () => {
        this.openModal(groundPosition.qrCode);
      });
      
      area.on('pointerout', () => {
        this.timeout = setTimeout(() => {
          this.showTooltip = false;
          this.tooltipValues = null;
          this.timeout = null;
        }, 50);
      });

      for (const e of elements) {
        // @ts-ignore
        this.viewPort.addChild(e);
      }
    }
  }

  onToggleTip() {
    this.toggleTip = !this.toggleTip;
    this.toggleTipEvent.emit(this.toggleTip);
    this.openTip = false;
  }

  getMaxCoordinates(groundPositions) {
    const positionsX = [];
    const positionsY = [];
    let maxColumn = 0;
    let maxRow = 0;
    for (const gp of groundPositions) {
      if (gp.column > maxColumn) { maxColumn = gp.column; }
      if (gp.row > maxRow) { maxRow = gp.row; }
    }
    let countX = 0;
    while (countX <= maxColumn) {
      positionsX.push(countX);
      countX++;
    }
    let countY = 0;
    while (countY <= maxRow) {
      positionsY.push(countY);
      countY++;
    }

    return { positionsX, positionsY };
  }

 
}
